import type { OcrTemplate } from '@/features/ocr-templates';

/**
 * Renders OCR-template-flavoured A4 pages on a canvas, then hands each one
 * back as a JPEG blob — the same output shape the Kodak scanner produces
 * for a real document. Used by the dev-tools "Test PDF" form to build a
 * synthetic multi-template document for QA-ing the OCR pool dialog and
 * per-page winner selection.
 *
 * Output is always JPEG at 92% quality (mirrors the photo-scanning upload
 * format), so the resulting "PDF of images" round-trips through the same
 * pipeline as a scanned document.
 */

const A4_MM = { width: 210, height: 297 } as const;

/** Convert a DPI to A4 pixel dimensions. */
export function a4Dimensions(dpi: number): { width: number; height: number } {
  const mmToInch = 1 / 25.4;
  return {
    width: Math.round(A4_MM.width * mmToInch * dpi),
    height: Math.round(A4_MM.height * mmToInch * dpi)
  };
}

export interface TemplatePageSpec {
  template: OcrTemplate;
  /** 1-based page index within this section (i.e. within this template). */
  sectionPageNumber: number;
  /** Total pages in this template's section. */
  sectionTotalPages: number;
  /** 1-based page index across the entire document. */
  globalPageNumber: number;
  /** Field labels rendered as `Label: value` rows. */
  fieldLabels: string[];
}

export interface GeneratedTemplatePage {
  globalPageNumber: number;
  templateId: number;
  blob: Blob;
}

export interface GenerateTemplatePagesOptions {
  pages: TemplatePageSpec[];
  widthPx: number;
  heightPx: number;
  /** Reports 0..1 progress so the UI can render a determinate bar. */
  onProgress?: (fraction: number) => void;
}

export async function generateTemplatePages(
  opts: GenerateTemplatePagesOptions
): Promise<GeneratedTemplatePage[]> {
  const output: GeneratedTemplatePage[] = [];
  for (let i = 0; i < opts.pages.length; i++) {
    const spec = opts.pages[i];
    const blob = await renderPage(spec, opts.widthPx, opts.heightPx);
    output.push({
      globalPageNumber: spec.globalPageNumber,
      templateId: spec.template.id,
      blob
    });
    opts.onProgress?.((i + 1) / opts.pages.length);
  }
  return output;
}

async function renderPage(
  spec: TemplatePageSpec,
  widthPx: number,
  heightPx: number
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context is not available in this browser');
  }

  // ── Background ────────────────────────────────────────────────────────
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const margin = Math.round(canvas.width * 0.05);
  ctx.strokeStyle = '#1f2937';
  ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.003));
  ctx.strokeRect(
    margin,
    margin,
    canvas.width - margin * 2,
    canvas.height - margin * 2
  );

  // ── Header band ───────────────────────────────────────────────────────
  // Big enough to comfortably fit Arabic title glyphs (which sit taller
  // than the Latin baseline).
  const headerHeight = Math.round(canvas.height * 0.1);
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(margin, margin, canvas.width - margin * 2, headerHeight);

  ctx.fillStyle = '#ffffff';
  // Browsers fall back through font stack until one renders Arabic
  // glyphs — "Tahoma" / "Segoe UI" / "Arial" all carry the codepoints
  // on Windows + macOS + Linux.
  ctx.font = `bold ${Math.round(canvas.width * 0.034)}px "Segoe UI", Tahoma, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(spec.template.name, canvas.width / 2, margin + headerHeight / 2);

  // Sub-header line — page-in-section + global page number.
  ctx.fillStyle = '#475569';
  ctx.textAlign = 'center';
  ctx.font = `${Math.round(canvas.width * 0.018)}px "Segoe UI", Tahoma, Arial, sans-serif`;
  ctx.fillText(
    `Section ${spec.sectionPageNumber} / ${spec.sectionTotalPages}   ·   Document page ${spec.globalPageNumber}`,
    canvas.width / 2,
    margin + headerHeight + Math.round(canvas.height * 0.025)
  );

  // ── Optional description (italic, muted) ──────────────────────────────
  let cursorY = margin + headerHeight + Math.round(canvas.height * 0.055);
  const description = (spec.template.description ?? '').trim();
  if (description !== '') {
    ctx.fillStyle = '#334155';
    ctx.textAlign = 'start';
    ctx.font = `italic ${Math.round(canvas.width * 0.019)}px "Segoe UI", Tahoma, Arial, sans-serif`;
    const lines = wrapText(
      ctx,
      description.slice(0, 300),
      canvas.width - margin * 2 - Math.round(canvas.width * 0.05)
    );
    const lineHeight = Math.round(canvas.height * 0.028);
    for (const line of lines.slice(0, 3)) {
      ctx.fillText(line, margin + Math.round(canvas.width * 0.025), cursorY);
      cursorY += lineHeight;
    }
    cursorY += Math.round(lineHeight * 0.5);
  }

  // ── Field rows (Label: value) ─────────────────────────────────────────
  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `bold ${Math.round(canvas.width * 0.022)}px "Segoe UI", Tahoma, Arial, sans-serif`;

  const fieldX = margin + Math.round(canvas.width * 0.025);
  const lineHeight = Math.round(canvas.height * 0.038);
  const labels =
    spec.fieldLabels.length > 0
      ? spec.fieldLabels
      : ['Field A', 'Field B', 'Field C'];

  for (let i = 0; i < labels.length; i++) {
    const label = labels[i];
    const value = makeDummyValue(
      spec.template.id,
      spec.sectionPageNumber,
      i,
      label
    );

    ctx.fillStyle = '#0f172a';
    ctx.font = `bold ${Math.round(canvas.width * 0.022)}px "Segoe UI", Tahoma, Arial, sans-serif`;
    ctx.fillText(`${label}:`, fieldX, cursorY);

    ctx.fillStyle = '#1e293b';
    ctx.font = `${Math.round(canvas.width * 0.022)}px "Segoe UI", Tahoma, Arial, sans-serif`;
    ctx.fillText(value, fieldX + Math.round(canvas.width * 0.32), cursorY);

    cursorY += lineHeight;
    if (cursorY > canvas.height - margin - Math.round(canvas.height * 0.08)) {
      break;
    }
  }

  // ── Footer ────────────────────────────────────────────────────────────
  ctx.fillStyle = '#94a3b8';
  ctx.font = `${Math.round(canvas.width * 0.014)}px "Segoe UI", Tahoma, Arial, sans-serif`;
  ctx.textAlign = 'start';
  ctx.fillText(
    'Generated by Safha dev-tools · for testing only',
    fieldX,
    canvas.height - margin - Math.round(canvas.height * 0.015)
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas toBlob returned null'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.92
    );
  });
}

/**
 * Deterministic per (template, page, field) so two consecutive renders
 * produce byte-identical output — useful when QA is diffing.
 */
function makeDummyValue(
  templateId: number,
  pageInSection: number,
  fieldIndex: number,
  label: string
): string {
  const seed =
    (templateId * 73856093) ^
    (pageInSection * 19349663) ^
    (fieldIndex * 83492791);
  const token = (Math.abs(seed) >>> 0).toString(16).toUpperCase();
  return `${label} ${pageInSection}-${token}`;
}

/** Naive word-wrap suitable for short descriptions on a canvas. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current === '' ? word : `${current} ${word}`;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      if (current !== '') lines.push(current);
      current = word;
    }
  }
  if (current !== '') lines.push(current);
  return lines;
}

// ─── Output helpers (mirror generate-container-pages) ────────────────────

/** Pack every rendered page into a single A4 PDF (one image per page). */
export async function downloadTemplatePagesAsPdf(
  pages: GeneratedTemplatePage[],
  filename: string
): Promise<void> {
  if (pages.length === 0) return;
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'p' });
  const pageWidthPt = doc.internal.pageSize.getWidth();
  const pageHeightPt = doc.internal.pageSize.getHeight();

  for (let i = 0; i < pages.length; i++) {
    if (i > 0) doc.addPage('a4', 'p');
    const dataUrl = await blobToDataUrl(pages[i].blob);
    doc.addImage(
      dataUrl,
      'JPEG',
      0,
      0,
      pageWidthPt,
      pageHeightPt,
      undefined,
      'FAST'
    );
  }

  doc.save(filename);
}

/** Trigger one browser download per generated page (matches scanner output). */
export function downloadTemplatePagesAsImages(
  pages: GeneratedTemplatePage[],
  prefix: string
): void {
  pages.forEach((page, idx) => {
    setTimeout(() => {
      const url = URL.createObjectURL(page.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${prefix}-P${String(page.globalPageNumber).padStart(3, '0')}.jpg`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }, idx * 120);
  });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}
